import type { OrderCostPlanLineStock } from '../../../types/orderWarehouseSummary';

/**
 * Why a line on the warehouse is still priced at the average purchase cost
 * (owner decision 2026-10-05): the WP5 plan takes free stock in confirmation
 * order, so stock held for earlier-confirmed orders is not this order's.
 * Quantities only (line unit), straight from the plan — nothing recomputed.
 *
 * - `held`: stock on hand, part of it waits for earlier-confirmed orders;
 * - `short`: stock on hand is free but smaller than the order;
 * - `none`: nothing on hand (classified here, but not rendered as a note:
 *   the line's average-cost tag already says it; see OrderMarginExplanation).
 */
export interface OrderMarginQueueExplanation {
  kind: 'held' | 'short' | 'none';
  onHand: number;
  held: number;
  shortfall: number;
}

export function getOrderMarginQueueExplanation(
  stock: OrderCostPlanLineStock | null | undefined,
): OrderMarginQueueExplanation | null {
  if (!stock || stock.onHand == null) return null;
  const onHand = stock.onHand;
  const held = stock.heldForEarlier ?? 0;
  const shortfall = stock.shortfall ?? 0;
  if (!(onHand > 0)) return { kind: 'none', onHand: 0, held: 0, shortfall };
  if (held > 0) return { kind: 'held', onHand, held, shortfall };
  return { kind: 'short', onHand, held: 0, shortfall };
}
